import React, { useEffect, useState } from "react";
import { useNavigate, useMatch } from "react-router-dom";
import { auth, db } from "../firebase";
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Box,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useSurveyRole, Role } from "../hooks/useSurveyRole";
import { doc, getDoc } from "firebase/firestore";

export default function TopBar() {
  const navigate = useNavigate();

  // Route matches
  const manageMatch = useMatch("/surveys/:id");
  const editMatch = useMatch("/surveys/:id/edit");
  const viewMatch = useMatch("/view/:id");
  const analyticsMatch = useMatch("/analytics/:id");
  const analyticsAltMatch = useMatch("/analyticsalt/:id");
  const dashboardMatch = useMatch("/dashboard/:id");
  const networkMatch = useMatch("/network/:id/*");

  const surveyId =
    manageMatch?.params.id ??
    editMatch?.params.id ??
    viewMatch?.params.id ??
    analyticsMatch?.params.id ??
    analyticsAltMatch?.params.id ??
    dashboardMatch?.params.id ??
    networkMatch?.params.id;

  const { role: effectiveRole, isAdmin } = useSurveyRole(surveyId);
  const [collapsed, setCollapsed] = useState(false);
  const [hasUsedToggle, setHasUsedToggle] = useState(false);
  const [accountIsAdmin, setAccountIsAdmin] = useState(false);

  useEffect(() => {
    const loadAdmin = async () => {
      const user = auth.currentUser;
      if (!user) {
        setAccountIsAdmin(false);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const role = snap.exists() ? (snap.data() as any)?.accountRole : undefined;
        setAccountIsAdmin(role === "admin");
      } catch {
        setAccountIsAdmin(false);
      }
    };
    loadAdmin();
  }, []);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/login", { replace: true });
    } catch (err: any) {
      console.error("Failed to log out:", err);
    }
  };

  const handlePreview = () => {
    if (!surveyId) return;
    const url = `${window.location.origin}/view/${surveyId}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleViewNetwork = () => {
    if (!surveyId) return;
    navigate(`/network/${surveyId}`);
  };

  return (
    <>
      {/* Whole TopBar hidden when collapsed */}
      <Box
        sx={{
          transform: collapsed ? "translateY(-100%)" : "translateY(0)",
          transition: "transform 0.4s ease",
          zIndex: 1200,
          position: collapsed ? "absolute" : "relative",
          width: "100%",
          top: collapsed ? 0 : "auto",
          left: 0,
        }}
      >
        <AppBar position="static">
          {/* Primary nav */}
          <Toolbar variant="dense" sx={{ px: 1, minHeight: 48 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{ cursor: "pointer", fontSize: "1rem" }}
              onClick={() => navigate("/")}
            >
              Home
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" color="inherit" onClick={() => navigate("/surveys")}>
              Manage Surveys
            </Button>
            •
            <Button size="small" color="inherit" onClick={() => navigate("/lists")}>
              Manage Lists
            </Button>
            •
            <Button size="small" color="inherit" onClick={() => navigate("/account")}>
              Manage Account
            </Button>
            {accountIsAdmin && (
              <>
                •
                <Button size="small" color="inherit" onClick={() => navigate("/admin")}>
                  Admin Tools
                </Button>
              </>
            )}
            •
            <Button size="small" color="inherit" onClick={() => navigate("/help")}>
              Help
            </Button>
            •
            <Button size="small" color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Toolbar>

          {/* Contextual submenu */}
          {surveyId && (
            <Toolbar
              variant="dense"
              sx={{
                px: 1,
                minHeight: 32,
                justifyContent: "flex-end",
                borderTop: "1px solid rgba(255,255,255,0.3)",
                alignItems: "center",
                transition: "all 0.3s ease",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                {(effectiveRole === "owner" || effectiveRole === "admin" || effectiveRole === "editor") && (
                  <>
                    <Button
                      size="small"
                      color="inherit"
                      variant={manageMatch ? "outlined" : "text"}
                      onClick={() => navigate(`/surveys/${surveyId}`)}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      Manage
                    </Button>
                    <Typography variant="body2" sx={{ mt: "-1px" }}>
                      •
                    </Typography>
                  </>
                )}

                {(effectiveRole === "owner" || effectiveRole === "editor" || effectiveRole === "admin") && (
                  <>
                    <Button
                      size="small"
                      color="inherit"
                      variant={editMatch ? "outlined" : "text"}
                      onClick={() => navigate(`/surveys/${surveyId}/edit`)}
                      sx={{ fontSize: "0.875rem" }}
                    >
                      Design
                    </Button>
                    <Typography variant="body2" sx={{ mt: "-1px" }}>
                      •
                    </Typography>
                  </>
                )}

                <Button
                  size="small"
                  color="inherit"
                  variant={(analyticsMatch || analyticsAltMatch) ? "outlined" : "text"}
                  onClick={() => navigate(`/analytics/${surveyId}`)}
                  sx={{ fontSize: "0.875rem" }}
                >
                  Data Export
                </Button>
                <Typography variant="body2" sx={{ mt: "-1px" }}>
                  •
                </Typography>

                <Button
                  size="small"
                  color="inherit"
                  variant={dashboardMatch ? "outlined" : "text"}
                  onClick={() => navigate(`/dashboard/${surveyId}`)}
                  sx={{ fontSize: "0.875rem" }}
                >
                  Charts
                </Button>
                <Typography variant="body2" sx={{ mt: "-1px" }}>
                  •
                </Typography>

                <Button
                  size="small"
                  color="inherit"
                  variant={networkMatch ? "outlined" : "text"}
                  onClick={handleViewNetwork}
                  sx={{ fontSize: "0.875rem" }}
                >
                  View Network
                </Button>
                <Typography variant="body2" sx={{ mt: "-1px" }}>
                  •
                </Typography>

                <Button
                  size="small"
                  color="inherit"
                  endIcon={<OpenInNewIcon sx={{ fontSize: "1rem" }} />}
                  onClick={handlePreview}
                  sx={{ fontSize: "0.875rem" }}
                >
                  Complete Survey
                </Button>

                {/* Inline Hide Menu toggle */}
                <Box
                  sx={{
                    ml: 1,
                    color: "white",
                    cursor: "pointer",
                    fontSize: "0.8rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.3,
                    "&:hover": { opacity: 0.8 },
                  }}
                  onClick={() => {
                    setCollapsed(true);
                    setHasUsedToggle(true);
                  }}
                >
                  {!hasUsedToggle ? "Hide Menu" : ""}
                  ▲
                </Box>
              </Box>
            </Toolbar>
          )}
        </AppBar>
      </Box>

      {/* Floating Show Menu when collapsed (global) */}
      {collapsed && (
        <Box
          sx={{
            position: "fixed",
            top: 8,
            right: 16,
            color: "white",
            backgroundColor: "rgba(25,118,210,0.95)",
            borderRadius: "20px",
            px: 1.5,
            py: 0.6,
            cursor: "pointer",
            fontSize: "0.8rem",
            fontWeight: 500,
            letterSpacing: "0.02em",
            boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            transition: "all 0.3s ease",
            zIndex: 1300,
            opacity: 0.75,
            userSelect: "none",
            "&:hover": {
              opacity: 1,
              backgroundColor: "rgba(25,118,210,1)",
              boxShadow: "0 3px 8px rgba(0,0,0,0.35)",
            },
          }}
          onClick={() => setCollapsed(false)}
        >
          ▼ Show Menu
        </Box>
      )}
    </>
  );
}
